import * as React from 'react'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import { Check, ChevronsUpDown } from 'lucide-react'

import { cn } from '@klear/ui/lib/utils'
import { Button } from '@klear/ui/components/button'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@klear/ui/components/command'
import { Popover, PopoverContent, PopoverTrigger } from '@klear/ui/components/popover'

export interface ComboboxOption<T = string> {
  value: T
  label: React.ReactNode
  keywords?: string[]
  disabled?: boolean
}

interface ComboboxProps<T = string> extends Omit<React.ComponentPropsWithoutRef<typeof Button>, 'onChange' | 'value'> {
  options?: ComboboxOption<T>[]
  onChange?: (value: T | '') => void
  value?: T | ''
  disabled?: boolean
  placeholder?: string
  triggerLabel?: string
  triggerIcon?: React.ReactNode
  error?: { message?: string }
  valueType?: 'string' | 'number' | 'object'
  className?: string
}

function ComboboxComponent<T = string>(props: ComboboxProps<T>, ref: React.ForwardedRef<HTMLButtonElement>) {
  const {
    options: initialOptions,
    value,
    onChange,
    disabled = false,
    placeholder = 'Search...',
    triggerLabel = 'Select...',
    triggerIcon = null,
    error,
    valueType = 'string',
    className
  } = props

  const [open, setOpen] = React.useState(false)

  const options = React.useMemo(
    () =>
      (initialOptions || []).map((option, index) => {
        const derivedKeywords = [
          ...(option.keywords ?? []),
          ...(isString(option.label) ? [option.label] : []),
          isObject(option.value) ? `${index}` : `${option.value}`
        ]
        return { ...option, keywords: derivedKeywords }
      }),
    [initialOptions]
  )

  const isOptionSelected = React.useCallback(
    (opt: ComboboxOption<T>) => {
      if (value === '' || value == null) return false
      if (valueType !== 'object') return opt.value === value
      return isEqual(value, opt.value)
    },
    [value, valueType]
  )

  const selectedOption =
    value !== '' && value != null && options.length > 0 ? options.find((opt) => isOptionSelected(opt)) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          tabIndex={0}
          className={cn('justify-between w-full', error && 'border-red-500', className)}
          disabled={disabled}
        >
          {triggerIcon}
          <span className="truncate">
            {selectedOption
              ? selectedOption.label
              : options.length === 0 && value
                ? 'Loading...'
                : (triggerLabel ?? placeholder)}
          </span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder={placeholder} className="h-9" />
          <CommandList className="p-1">
            <CommandEmpty>No results.</CommandEmpty>
            {options.map((option, index) => {
              const optionKey = valueType === 'object' ? `${index}` : `${option.value}`
              const isSelected = isOptionSelected(option)
              const isDisabled = Boolean(option.disabled)
              return (
                <CommandItem
                  key={optionKey}
                  disabled={isDisabled}
                  className={cn('cursor-pointer', isDisabled && 'text-muted-foreground')}
                  value={optionKey}
                  keywords={option.keywords}
                  onSelect={() => {
                    if (isDisabled) return
                    onChange?.(isSelected ? '' : (option.value as T | ''))
                    setOpen(false)
                  }}
                >
                  {option.label}
                  <Check className={cn('ml-auto', isSelected ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export const Combobox = React.forwardRef(ComboboxComponent) as unknown as {
  <T = string>(props: ComboboxProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }): JSX.Element
  displayName?: string
}

Combobox.displayName = 'Combobox'
